// src/runtime/RuntimeDispatcher.ts
// ============================================
// RuntimeDispatcher - Runtime 请求分发器（冻结版本 v1.0）
// 
// 职责（Only 3 things）：
//   1. 分发 Query / Execute
//   2. 记录 Latency（O(1) 统计）
//   3. 超时控制
//
// 不负责：
//   - 日志（由 Logger 注入）
//   - 响应包装（由 RuntimeManager 包装）
//   - 权限/缓存/重试（由 Middleware 处理）
// ============================================

import {
  RuntimeName,
  RuntimeResponse,
  IRuntime,
} from '../types/runtime';

// ============================================
// Logger 接口（依赖注入）
// ============================================
export interface Logger {
  info(message: string, ...args: unknown[]): void;
  warn(message: string, ...args: unknown[]): void;
  error(message: string, ...args: unknown[]): void;
  debug(message: string, ...args: unknown[]): void;
}

// ============================================
// 延迟统计数据（O(1) 维护）
// ============================================
export interface LatencyStats {
  count: number;
  sum: number;
  avg: number;
  min: number;
  max: number;
}

// ============================================
// 分发配置
// ============================================
export interface DispatcherConfig {
  timeout?: number;          // 默认超时（毫秒）
  maxLatencyHistory?: number; // 最大记录数
}

// ============================================
// 中间件接口
// ============================================
export type DispatchContext = {
  runtime: IRuntime;
  action: 'query' | 'execute';
  payload?: unknown;
  startTime: number;
  abortController?: AbortController;
};

export type Middleware = (
  context: DispatchContext,
  next: () => Promise<unknown>
) => Promise<unknown>;

// ============================================
// RuntimeDispatcher
// ============================================
export class RuntimeDispatcher {
  private latencyStats: Map<RuntimeName, LatencyStats> = new Map();
  private maxHistory: number = 500;
  private defaultTimeout: number = 120000; // 120s（图片/视频生成可能需要较长时间）
  private middlewares: Middleware[] = [];
  private logger?: Logger;

  constructor(config: DispatcherConfig = {}, logger?: Logger) {
    this.defaultTimeout = config.timeout ?? 30000;
    this.maxHistory = config.maxLatencyHistory ?? 500;
    this.logger = logger;
  }

  // ============================================
  // 依赖注入
  // ============================================
  setLogger(logger: Logger): void {
    this.logger = logger;
  }

  // ============================================
  // 中间件
  // ============================================
  use(middleware: Middleware): void {
    this.middlewares.push(middleware);
  }

  // ============================================
  // 超时配置
  // ============================================
  setTimeout(timeout: number): void {
    this.defaultTimeout = timeout;
  }

  // ============================================
  // Query
  // ============================================
  async query(
    runtime: IRuntime,
    params?: Record<string, unknown>,
    timeout?: number
  ): Promise<unknown> {
    return this.dispatchQuery(runtime, params, timeout);
  }

  // ============================================
  // Execute
  // ============================================
  async execute(
    runtime: IRuntime,
    command: string,
    params?: Record<string, unknown>,
    timeout?: number
  ): Promise<unknown> {
    return this.dispatchExecute(runtime, command, params, timeout);
  }

  // ============================================
  // 私有分发方法
  // ============================================

  private async dispatchQuery(
    runtime: IRuntime,
    params?: Record<string, unknown>,
    timeout?: number
  ): Promise<unknown> {
    if (!runtime.query) {
      throw new Error(
        `Runtime "${runtime.name}" does not support query`
      );
    }

    const context: DispatchContext = {
      runtime,
      action: 'query',
      payload: params,
      startTime: performance.now(),
    };

    return this.runWithMiddleware(context, async () => {
      return this.runWithTimeout(
        () => runtime.query!(params),
        timeout ?? this.defaultTimeout
      );
    });
  }

  private async dispatchExecute(
    runtime: IRuntime,
    command: string,
    params?: Record<string, unknown>,
    timeout?: number
  ): Promise<unknown> {
    if (!runtime.execute) {
      throw new Error(
        `Runtime "${runtime.name}" does not support execute`
      );
    }

    const context: DispatchContext = {
      runtime,
      action: 'execute',
      payload: { command, params },
      startTime: performance.now(),
    };

    return this.runWithMiddleware(context, async () => {
      return this.runWithTimeout(
        () => runtime.execute!(command, params),
        timeout ?? this.defaultTimeout
      );
    });
  }

  // ============================================
  // 中间件执行器
  // ============================================
  private async runWithMiddleware(
    context: DispatchContext,
    handler: () => Promise<unknown>
  ): Promise<unknown> {
    if (this.middlewares.length === 0) {
      return handler();
    }

    let index = 0;

    const next = async (): Promise<unknown> => {
      if (index >= this.middlewares.length) {
        return handler();
      }
      const middleware = this.middlewares[index++];
      return middleware(context, next);
    };

    return next();
  }

  // ============================================
  // 超时执行器
  // ============================================
  private async runWithTimeout<T>(
    fn: () => Promise<T>,
    timeout: number
  ): Promise<T> {
    if (timeout <= 0) {
      return fn();
    }

    const abortController = new AbortController();
    const timeoutId = setTimeout(() => {
      abortController.abort();
    }, timeout);

    try {
      const result = await fn();
      clearTimeout(timeoutId);
      return result;
    } catch (error) {
      clearTimeout(timeoutId);
      if (abortController.signal.aborted) {
        throw new Error(`Request timed out after ${timeout}ms`);
      }
      throw error;
    }
  }

  // ============================================
  // 延迟统计（O(1)）
  // ============================================
  recordLatency(name: RuntimeName, latency: number): void {
    const existing = this.latencyStats.get(name);

    if (!existing) {
      this.latencyStats.set(name, {
        count: 1,
        sum: latency,
        avg: latency,
        min: latency,
        max: latency,
      });
      return;
    }

    // 限制最大记录数（移除最旧的一条）
    if (existing.count >= this.maxHistory) {
      // 简单策略：重置统计（保持 O(1)）
      // 更精确的策略可以维护滑动窗口，但这里保持简单
      this.latencyStats.set(name, {
        count: 1,
        sum: latency,
        avg: latency,
        min: latency,
        max: latency,
      });
      this.logger?.debug(
        `[RuntimeDispatcher] Latency stats reset for ${name} (max ${this.maxHistory} reached)`
      );
      return;
    }

    this.latencyStats.set(name, {
      count: existing.count + 1,
      sum: existing.sum + latency,
      avg: (existing.sum + latency) / (existing.count + 1),
      min: Math.min(existing.min, latency),
      max: Math.max(existing.max, latency),
    });
  }

  // ============================================
  // 延迟统计查询（O(1)）
  // ============================================
  getLatencyStats(): Map<RuntimeName, LatencyStats> {
    return new Map(this.latencyStats);
  }

  /**
   * 获取单个 Runtime 的延迟统计
   */
  getLatencyFor(name: RuntimeName): LatencyStats | undefined {
    return this.latencyStats.get(name);
  }

  /**
   * 清空延迟统计
   */
  clearLatencyHistory(): void {
    this.latencyStats.clear();
    this.logger?.debug('[RuntimeDispatcher] Latency stats cleared');
  }

  // ============================================
  // 工具
  // ============================================
  reset(): void {
    this.latencyStats.clear();
    this.middlewares = [];
    this.logger?.debug('[RuntimeDispatcher] Reset');
  }

  destroy(): void {
    this.latencyStats.clear();
    this.middlewares = [];
    this.logger = undefined;
  }
}

export default RuntimeDispatcher;